import { useTranslation } from 'react-i18next';
import { Button } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import NoDataState from '@/enterprise/components/NoDataState';
import Skeleton from '@/enterprise/components/Skeleton';
import { Table, TableBody, TableDisplaySkeletonRows, TablePagination } from '@/enterprise/components/Table';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { buildCursorResetKey, useCursorPagination } from '@/utils/useCursorPagination';
import { useStateResetOn } from '@/utils/useStateResetOn';
import useUrlState from '@/utils/useUrlState';
import AwardedInvoiceAmount from './AwardedInvoiceAmount';
import { AwardedInvoicesTable } from './AwardedInvoicesTable';
import { AwardedInvoiceActions, AwardedInvoiceSortKey, AwardedInvoiceSource, AwardedInvoiceUrlState } from './types';

const AwardedInvoicesView = ({
  source,
  actions,
}: {
  source: AwardedInvoiceSource;
  actions?: AwardedInvoiceActions;
}) => {
  const { t } = useTranslation();

  const [urlState, setUrlState] = useUrlState<AwardedInvoiceUrlState>({
    pageSize: 25,
    sort: { key: 'newPayDate', direction: 'desc' },
  });

  // Reason: sort and page size live in the URL, which can change without this view unmounting
  // (back navigation). Keying the cursors on those inputs keeps them in step with what was
  // actually queried, so no handler has to remember to reset.
  const queryInputsKey = buildCursorResetKey(urlState);

  const { currentCursor, currentPage, canGoBack, goToNextPage, goToPreviousPage } = useCursorPagination({
    resetKey: queryInputsKey,
  });
  const [nextCursor, setNextCursor] = useStateResetOn<string | null>(queryInputsKey, null);

  const handleSort = (sortKey: AwardedInvoiceSortKey) => {
    // useUrlState returns Partial<S>, so sort.direction may be missing from the URL
    const updatedSortDirection: 'asc' | 'desc' = urlState.sort?.direction === 'asc' ? 'desc' : 'asc';
    if (urlState.sort && sortKey === urlState.sort.key) {
      actions?.onSort?.({ sortKey, sortDirection: updatedSortDirection });
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: updatedSortDirection,
        },
      }));
    } else {
      actions?.onSort?.({ sortKey, sortDirection: 'desc' });
      setUrlState((f) => ({
        ...f,
        sort: {
          key: sortKey,
          direction: 'desc',
        },
      }));
    }
  };

  const handleNextPage = () => {
    if (nextCursor) {
      actions?.onPageChange?.();
      goToNextPage(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    actions?.onPageChange?.();
    goToPreviousPage();
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const size = Number(e.target.value);
    actions?.onPageSizeChange?.({ size });
    setUrlState((f) => ({ ...f, pageSize: size }));
  };

  return (
    <div>
      <div className="border-y border-deepGray-500/[12%] bg-canvas">
        <div className="flex flex-wrap items-center gap-1 p-4">
          <div className="font-medium">{t('core.awarded')}:</div>
          <QueryBoundaries LoadingComponent={() => <Skeleton className="h-6 w-24" />}>
            <AwardedInvoiceAmount source={source} />
          </QueryBoundaries>
        </div>
      </div>
      <QueryBoundaries
        LoadingComponent={() => (
          <Table>
            <TableBody>
              <TableDisplaySkeletonRows colSpan={8} rowCount={10} />
            </TableBody>
          </Table>
        )}
        ErrorComponent={() => (
          <div className="px-4 pb-6 pt-8">
            <NoDataState
              icon={<TechnicalDifficultyIcon className="size-24 pb-4" />}
              title={t('invoices.errorLoadingInvoices')}
              message={t('invoices.errorLoadingInvoicesDescription')}
              action={
                <Button size="sm" variant="ancillary" onClick={() => window.location.reload()}>
                  {t('core.refresh')}
                </Button>
              }
            />
          </div>
        )}
      >
        <AwardedInvoicesTable
          currentCursor={currentCursor}
          setNextCursor={setNextCursor}
          urlState={urlState as AwardedInvoiceUrlState}
          handleSort={handleSort}
          source={source}
        />
      </QueryBoundaries>
      <TablePagination
        className="p-6"
        onNextChange={handleNextPage}
        onPrevChange={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
        pageSize={urlState.pageSize ?? 25}
        disablePrev={!canGoBack}
        disableNext={!nextCursor}
        currentPage={currentPage}
      />
    </div>
  );
};

export default AwardedInvoicesView;
